import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toFormErrors, type FormErrors } from '../auth/errors';
import { useAuth } from '../auth/useAuth';
import { Alert, Button, Input } from '../components/ui';
import { AuthLayout } from './AuthLayout';

const NO_ERRORS: FormErrors = { fields: {}, message: null };

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrors(NO_ERRORS);
    setIsSubmitting(true);

    try {
      await signIn(email, password);
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? '/', { replace: true });
    } catch (error) {
      setErrors(toFormErrors(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to pick up where you left off."
      footer={
        <>
          New here? <Link to="/register">Create an account</Link>
        </>
      }
    >
      <form className="sc-form" onSubmit={handleSubmit} noValidate>
        {errors.message && <Alert tone="danger">{errors.message}</Alert>}

        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          error={errors.fields['email']}
        />

        <Input
          label="Password"
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          error={errors.fields['password']}
        />

        <div className="sc-form__actions">
          <Button type="submit" isLoading={isSubmitting} loadingLabel="Signing in" fullWidth>
            Sign in
          </Button>
        </div>

        <div className="sc-form__links">
          <Link to="/forgot-password">Forgot your password?</Link>
        </div>
      </form>
    </AuthLayout>
  );
}
